import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import type { SelectableOption } from '../axisNewClientConstants';

interface AxisOptionSelectorProps<OptionId extends string> {
  question: string;
  options: Array<SelectableOption<OptionId>>;
  selectedId: OptionId | null;
  onSelect: (id: OptionId) => void;
}

export const AxisOptionSelector = <OptionId extends string>({
  question,
  options,
  selectedId,
  onSelect,
}: AxisOptionSelectorProps<OptionId>) => {
  return (
    <SelectorRoot>
      <SelectorQuestion>{question}</SelectorQuestion>
      <OptionsGrid>
        {options.map((option) => {
          const isSelected = selectedId === option.id;
          return (
            <OptionButton
              key={option.id}
              type='button'
              $selected={isSelected}
              onClick={() => {
                onSelect(option.id);
              }}
            >
              <OptionLabel>{option.label}</OptionLabel>
            </OptionButton>
          );
        })}
      </OptionsGrid>
    </SelectorRoot>
  );
};

const SelectorRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  textAlign: 'left',
}));

const SelectorQuestion = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle1,
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightMedium,
}));

const OptionsGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))',
  gap: theme.spacing(1.5),
}));

const OptionButton = styled('button', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  ...theme.typography.body2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: theme.spacing(6),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${
    $selected ? theme.palette.primary.main : theme.palette.divider
  }`,
  backgroundColor: $selected
    ? theme.palette.action.selected
    : theme.palette.background.paper,
  color: theme.palette.text.primary,
  cursor: 'pointer',
  textAlign: 'center',
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const OptionLabel = styled('span')({
  lineHeight: 1.3,
});
